import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { getSupabase } from "@/lib/supabase";
import { trackEvent } from "@/lib/analytics";
import { featuredProjects } from "@/mocks/projects";
import type { Project as MockProject } from "@/mocks/projects";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

interface DbProject {
  id: number;
  client_id: string;
  title: string;
  description: string;
  budget: string;
  category: string;
  status: string;
  created_at: string;
  payment_method: string | null;
  profiles: {
    username: string | null;
    display_name: string | null;
  } | null;
}

interface DisplayProject {
  id: number;
  title: string;
  author: string;
  authorAvatar: string;
  date: string;
  budget: string;
  category: string;
  paymentMethod: string | null;
}

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const projectsListSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  url: `${siteUrl}/projects-listing`,
  name: "Recent Projects — Hackers Lodge",
  description: "Browse available hacking projects posted by clients. Find penetration testing, database recovery, social engineering, and other cybersecurity projects.",
  isPartOf: {
    "@type": "WebSite",
    name: "Hackers Lodge",
    url: siteUrl,
  },
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState<DisplayProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    // Fire GA4 secondary: service_page_view
    trackEvent("service_page_view", { page: "projects_listing" });

    const supabase = getSupabase();
    setLoading(true);
    setFetchError("");

    supabase
      .from("projects")
      .select("*, profiles:client_id(username, display_name)")
      .eq("review_status", "approved")
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          setFetchError(error.message);
          // Fall back to mock data
          const mockDisplay: DisplayProject[] = featuredProjects.map((p: MockProject) => ({
            ...p,
          }));
          setProjects(mockDisplay);
        } else if (data && data.length > 0) {
          const displayData: DisplayProject[] = (data as unknown as DbProject[]).map((p) => ({
            id: p.id,
            title: p.title,
            author: p.profiles?.username || p.profiles?.display_name || "Anonymous",
            authorAvatar: "https://hackerslodge.com/wp-content/uploads/2026/01/user-150x150-1.png",
            date: formatDate(p.created_at),
            budget: p.budget,
            category: p.category,
            paymentMethod: p.payment_method || null,
          }));
          setProjects(displayData);
        } else {
          // Empty table — fall back to mock data
          const mockDisplay: DisplayProject[] = featuredProjects.map((p: MockProject) => ({
            ...p,
          }));
          setProjects(mockDisplay);
        }
        setLoading(false);
      })
      .catch(() => {
        setFetchError("Failed to load projects. Showing sample data instead.");
        const mockDisplay: DisplayProject[] = featuredProjects.map((p: MockProject) => ({
          ...p,
        }));
        setProjects(mockDisplay);
        setLoading(false);
      });
  }, []);

  const totalPages = Math.max(1, Math.ceil(projects.length / itemsPerPage));
  const startIdx = (activePage - 1) * itemsPerPage;
  const visibleProjects = projects.slice(startIdx, startIdx + itemsPerPage);

  const handlePageClick = (page: number) => {
    setActivePage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatPaymentMethod = (method: string | null | undefined): string => {
    if (!method) return "";
    if (method === "crypto") return "Crypto";
    if (method === "escrow") return "Escrow";
    if (method === "wire") return "Wire";
    return method;
  };

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <PageMeta
        title="Recent Cybersecurity Projects — Browse &amp; Bid on Hacking Projects"
        description="Browse available hacking projects posted by clients. Find penetration testing, database recovery, social engineering, website security, mobile hacking, and other cybersecurity projects. Post your own project and get matched with top hackers."
        keywords="hacking projects, cybersecurity projects, penetration testing jobs, ethical hacking jobs, bug bounty projects, security audit projects"
        canonicalUrl={`${siteUrl}/projects-listing`}
        ogType="website"
        schema={projectsListSchema}
      />
      <Header />
      <main className="pt-20 md:pt-24">
        <section className="py-8 md:py-12 bg-[#161b22]">
          <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Projects" }]} />
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-light text-gray-200 mb-2">
                  Recent Projects
                </h1>
                <p className="text-sm text-gray-500">
                  Browse available hacking projects posted by clients
                </p>
              </div>
              <Link
                to="/post-project"
                onClick={() => trackEvent("post_project_click", { source: "projects_listing" })}
                className="inline-flex items-center gap-2 bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-5 py-2 rounded transition-all whitespace-nowrap"
              >
                <i className="fas fa-plus-circle" />
                Post a Project
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search projects by keyword..."
                  className="w-full px-4 py-3 pr-10 border border-gray-700 rounded text-sm focus:outline-none focus:border-[#5bc0de]"
                />
                <i className="fas fa-search absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
              <select className="px-4 py-3 border border-gray-700 rounded text-sm text-gray-400 focus:outline-none focus:border-[#5bc0de]">
                <option>All Categories</option>
                <option>Social Media Hack</option>
                <option>Penetration Testing</option>
                <option>Spy</option>
                <option>Database</option>
              </select>
              <select className="px-4 py-3 border border-gray-700 rounded text-sm text-gray-400 focus:outline-none focus:border-[#5bc0de]">
                <option>All Budgets</option>
                <option>Under $500</option>
                <option>$500 - $1,000</option>
                <option>$1,000 - $5,000</option>
                <option>Over $5,000</option>
              </select>
            </div>

            {fetchError && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                <i className="fas fa-exclamation-triangle mr-2" />{fetchError}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <i className="fas fa-spinner fa-spin text-2xl text-[#5bc0de]" />
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-[#1c2128] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-folder-open text-gray-400 text-xl" />
                </div>
                <p className="text-gray-500 mb-4">No projects posted yet.</p>
                <Link
                  to="/post-project"
                  onClick={() => trackEvent("post_project_click", { source: "projects_listing" })}
                  className="inline-flex items-center gap-2 bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-5 py-2 rounded transition-all whitespace-nowrap"
                >
                  <i className="fas fa-plus-circle" />
                  Be the first to post a project
                </Link>
              </div>
            ) : (
              <>
                <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-700 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div className="col-span-5">Project Title</div>
                  <div className="col-span-2">By</div>
                  <div className="col-span-1">Budget</div>
                  <div className="col-span-1">Payment</div>
                  <div className="col-span-1">Date</div>
                  <div className="col-span-2"></div>
                </div>

                <ul className="divide-y divide-gray-100 bg-[#0d1117] rounded border border-gray-700">
                  {visibleProjects.map((project) => (
                    <li key={project.id} className="py-4 hover:bg-[#161b22] transition-colors">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 items-center">
                        <div className="md:col-span-5 flex items-center gap-3 min-w-0">
                          <img
                            src={project.authorAvatar}
                            alt={project.author}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                          />
                          <Link
                            to={`/project/${project.id}`}
                            className="text-sm text-gray-200 hover:text-[#5bc0de] truncate font-medium"
                            title={project.title}
                          >
                            {project.title}
                          </Link>
                        </div>

                        <div className="md:col-span-2 hidden md:block">
                          <span className="text-sm text-gray-400">{project.author}</span>
                        </div>

                        <div className="md:col-span-2 hidden md:block">
                          <span className="text-sm text-gray-500">{project.date}</span>
                        </div>

                        <div className="md:col-span-1 hidden md:block">
                          <span className="text-sm font-semibold text-[#5cb85c]">{project.budget}</span>
                        </div>

                        <div className="md:col-span-1 hidden md:block">
                          {project.paymentMethod ? (
                            <span className="text-xs text-gray-400 capitalize">{formatPaymentMethod(project.paymentMethod)}</span>
                          ) : (
                            <span className="text-xs text-gray-600">—</span>
                          )}
                        </div>

                        <div className="md:col-span-1 hidden md:block">
                          <span className="text-sm text-gray-500">{project.date}</span>
                        </div>

                        <div className="md:col-span-2 flex justify-start md:justify-end">
                          <Link
                            to={`/project/${project.id}`}
                            className="inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-xs font-semibold px-4 py-1.5 rounded transition-all whitespace-nowrap"
                          >
                            View
                          </Link>
                        </div>

                        <div className="md:hidden flex items-center gap-4 text-xs text-gray-500">
                          <span>By {project.author}</span>
                          <span>{project.date}</span>
                          <span className="font-semibold text-[#5cb85c]">{project.budget}</span>
                          {project.paymentMethod && (
                            <span className="capitalize">{formatPaymentMethod(project.paymentMethod)}</span>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      const p = i + 1;
                      return (
                        <button
                          key={p}
                          onClick={() => handlePageClick(p)}
                          className={`w-8 h-8 flex items-center justify-center rounded text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                            activePage === p ? "bg-[#5bc0de] text-white" : "text-gray-400 hover:bg-[#1c2128]"
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                    {totalPages > 5 && (
                      <>
                        <span className="text-sm text-gray-400">...</span>
                        <button
                          onClick={() => handlePageClick(totalPages)}
                          className={`w-8 h-8 flex items-center justify-center rounded text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                            activePage === totalPages ? "bg-[#5bc0de] text-white" : "text-gray-400 hover:bg-[#1c2128]"
                          }`}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handlePageClick(Math.min(activePage + 1, totalPages))}
                      className="w-8 h-8 flex items-center justify-center rounded text-sm text-gray-400 hover:bg-[#1c2128] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="fas fa-angle-double-right" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}